// client/src/realtime/RealtimeProvider.tsx
// Provider component that manages realtime connection and cache updates

import { createContext, useContext, ReactNode } from 'react';
import {
  useRealtimeConnection,
  useWorkspaceChannel,
  useRealtimeCacheUpdates,
} from './index';

interface RealtimeContextValue {
  isConnected: boolean;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  isConnected: false,
});

export function useRealtimeContext() {
  return useContext(RealtimeContext);
}

interface RealtimeProviderProps {
  children: ReactNode;
  workspaceId: string | null | undefined;
}

/**
 * Provider component that manages WebSocket connection and cache updates.
 * Wrap your app with this after authentication to enable real-time features.
 *
 * @example
 * ```tsx
 * <RealtimeProvider workspaceId={activeWorkspaceId}>
 *   <App />
 * </RealtimeProvider>
 * ```
 */
export function RealtimeProvider({ children, workspaceId }: RealtimeProviderProps) {
  // Manage WebSocket connection
  const { isConnected } = useRealtimeConnection();

  // Subscribe to workspace channel
  useWorkspaceChannel(workspaceId);

  // Wire up RTK Query cache updates
  useRealtimeCacheUpdates(workspaceId);

  return (
    <RealtimeContext.Provider value={{ isConnected }}>
      {children}
    </RealtimeContext.Provider>
  );
}
